import React, { useState } from "react";
import { Loader2, RefreshCw, Timer } from "lucide-react";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { useApp } from "../context/AppContext";
import { useCountdown } from "../hooks/useCountdown";
import { formatCountdown } from "../lib/utils";

export function PaymentCountdown({ booking }) {
  const { t } = useApp();
  const active = booking.status === "confirmed" && booking.payment_status === "pending";
  const ms = useCountdown(active ? booking.payment_deadline : null);
  if (!active || ms == null) return null;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs tabular-nums ${
        ms < 120000 ? "border-destructive/50 bg-destructive/10 text-destructive" : "border-primary/50 bg-primary/10 text-primary"
      }`}
      data-testid={`payment-countdown-${booking.id}`}
    >
      <Timer className="h-3 w-3" />
      {ms === 0 ? t("payment_time_over") : `${t("payment_countdown")} ${formatCountdown(ms)}`}
    </span>
  );
}

export function PaidCheckbox({ booking, onMarkPaid }) {
  const { t } = useApp();
  const paid = booking.payment_status === "paid";
  if (!paid && ["cancelled", "rejected"].includes(booking.status)) return null;
  return (
    <>
    {booking.paid_after_cancel && (
      <span className="rounded-full border border-destructive/50 bg-destructive/10 px-2.5 py-1 text-xs text-destructive" data-testid={`paid-after-cancel-${booking.id}`}>
        {t("paid_after_cancel")}
      </span>
    )}
    <label
      className={`inline-flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
        paid
          ? "cursor-default border-[hsl(var(--success)/0.5)] bg-[hsl(var(--success)/0.15)] text-[hsl(var(--success))]"
          : "border-border bg-background/60 text-muted-foreground hover:border-[hsl(var(--success)/0.6)] hover:text-[hsl(var(--success))]"
      }`}
      title={paid ? undefined : t("mark_paid_tooltip")}
    >
      <Checkbox
        checked={paid}
        disabled={paid}
        onCheckedChange={(v) => { if (v && !paid) onMarkPaid(booking); }}
        data-testid={`paid-checkbox-${booking.id}`}
      />
      {t("paid_checkbox")}
    </label>
    </>
  );
}

export function CheckStripeButton({ booking, onCheck }) {
  const { t } = useApp();
  const [busy, setBusy] = useState(false);
  if (booking.payment_status === "paid" || !booking.payment_session_id || booking.payment_session_id === "manual") return null;
  const run = async () => {
    setBusy(true);
    try { await onCheck(booking); } finally { setBusy(false); }
  };
  return (
    <Button
      size="sm"
      variant="outline"
      className="h-7 gap-1.5 text-xs"
      onClick={run}
      disabled={busy}
      data-testid={`check-stripe-button-${booking.id}`}
    >
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
      {t("check_stripe_button")}
    </Button>
  );
}
